import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

/**
 * Asserts a value is not null or undefined.
 * Fails loudly instead of masking bugs with silent defaults.
 */
export function defined<T>(value: T | null | undefined, message?: string): T {
  if (value === null || value === undefined) {
    throw new Error(message ?? 'Expected value to be defined, but got ' + String(value));
  }
  return value;
}

export function extractErrorCode(message: string): string | null {
  const start = message.indexOf('[');
  const end = message.indexOf(']');

  if (start !== -1 && end !== -1) {
    return message.substring(start + 1, end);
  }

  return null;
}

export function isErrorWithMessage(error: unknown): error is { data: { message: string } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (error as any).data === 'object' &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'message' in (error as any).data
  );
}

/**
 * Checks if an error message indicates the device is not responding.
 * Matches both direct timeouts and queued request expirations.
 */
export function isDeviceNotRespondingError(errorMessage: string | null | undefined): boolean {
  if (!errorMessage) return false;
  const lower = errorMessage.toLowerCase();
  return lower.includes('timeout') || lower.includes('queued request expired');
}

/**
 * Calculate exponential backoff delay with jitter.
 * Jitter prevents thundering herd when multiple clients reconnect simultaneously.
 *
 * @param attempt - Current attempt number (0-based)
 * @param baseDelayMs - Base delay in milliseconds (default: 1000)
 * @param maxDelayMs - Maximum delay cap in milliseconds (default: 30000)
 * @param jitterFactor - Jitter as fraction of delay (default: 0.3 = ±30%)
 * @returns Delay in milliseconds with jitter applied
 */
export function calculateBackoffWithJitter(
  attempt: number,
  baseDelayMs: number = 1000,
  maxDelayMs: number = 30000,
  jitterFactor: number = 0.3,
): number {
  // Calculate base exponential delay
  const exponentialDelay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs);

  // Apply jitter: random value between -jitterFactor and +jitterFactor
  const jitter = exponentialDelay * jitterFactor * (Math.random() * 2 - 1);

  // Ensure delay is at least baseDelayMs / 2
  return Math.max(baseDelayMs / 2, Math.round(exponentialDelay + jitter));
}
